import {
  useMemo,
  useState,
  type PointerEvent as ReactPointerEvent,
} from "react";
import { motion } from "motion/react";

import { useCurrency } from "../../currency/CurrencyContext";
import type { SalesHistoryPoint } from "../types";

interface HistoryChartProps {
  id: string;
  points: SalesHistoryPoint[];
  selectedRange: HistoryChartRange | null;
  onRangeChange: (range: HistoryChartRange | null) => void;
}

export interface HistoryChartRange {
  startIndex: number;
  endIndex: number;
}

interface ChartPoint extends SalesHistoryPoint {
  x: number;
  y: number;
}

interface PathPoint {
  x: number;
  y: number;
}

interface SalesBar {
  x: number;
  y: number;
  width: number;
  height: number;
}

const WIDTH = 720;
const HEIGHT = 350;
const PATH_SAMPLE_COUNT = 72;
const SALES_SECTION_GAP = 18;
const SALES_SECTION_HEIGHT = 62;
const PADDING = {
  top: 20,
  right: 20,
  bottom: 36,
  left: 58,
};

function resamplePathPoints(points: ChartPoint[]): PathPoint[] {
  if (points.length === 0) {
    return [];
  }

  if (points.length === 1) {
    const plotWidth = WIDTH - PADDING.left - PADDING.right;
    return Array.from({ length: PATH_SAMPLE_COUNT }, (_, index) => ({
      x:
        PADDING.left +
        (index / Math.max(PATH_SAMPLE_COUNT - 1, 1)) * plotWidth,
      y: points[0].y,
    }));
  }

  return Array.from({ length: PATH_SAMPLE_COUNT }, (_, sampleIndex) => {
    const sourcePosition =
      (sampleIndex / Math.max(PATH_SAMPLE_COUNT - 1, 1)) *
      (points.length - 1);
    const leftIndex = Math.floor(sourcePosition);
    const rightIndex = Math.min(leftIndex + 1, points.length - 1);
    const progress = sourcePosition - leftIndex;
    const left = points[leftIndex];
    const right = points[rightIndex];

    return {
      x: left.x + (right.x - left.x) * progress,
      y: left.y + (right.y - left.y) * progress,
    };
  });
}

function createSmoothLinePath(points: PathPoint[]): string {
  if (points.length === 0) {
    return "";
  }
  if (points.length === 1) {
    return `M ${points[0].x.toFixed(2)} ${points[0].y.toFixed(2)}`;
  }

  return points.reduce((path, point, index) => {
    if (index === 0) {
      return `M ${point.x.toFixed(2)} ${point.y.toFixed(2)}`;
    }

    const previous = points[index - 1];
    const beforePrevious = points[index - 2] ?? previous;
    const next = points[index + 1] ?? point;
    const controlOneX = previous.x + (point.x - beforePrevious.x) / 6;
    const controlOneY = previous.y + (point.y - beforePrevious.y) / 6;
    const controlTwoX = point.x - (next.x - previous.x) / 6;
    const controlTwoY = point.y - (next.y - previous.y) / 6;

    return `${path} C ${controlOneX.toFixed(2)} ${controlOneY.toFixed(
      2,
    )}, ${controlTwoX.toFixed(2)} ${controlTwoY.toFixed(2)}, ${point.x.toFixed(
      2,
    )} ${point.y.toFixed(2)}`;
  }, "");
}

function normalizeRange(startIndex: number, endIndex: number): HistoryChartRange {
  return {
    startIndex: Math.min(startIndex, endIndex),
    endIndex: Math.max(startIndex, endIndex),
  };
}

export function HistoryChart({
  id,
  points,
  selectedRange,
  onRangeChange,
}: HistoryChartProps) {
  const { formatPrice } = useCurrency();
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [dragStartIndex, setDragStartIndex] = useState<number | null>(null);
  const [dragEndIndex, setDragEndIndex] = useState<number | null>(null);
  const chart = useMemo(() => {
    const activePrices = points
      .map((point) => point.avg_price)
      .filter((price) => price > 0);
    if (activePrices.length === 0) {
      return null;
    }

    const rawMin = Math.min(...activePrices);
    const rawMax = Math.max(...activePrices);
    const spread = Math.max(rawMax - rawMin, rawMax * 0.08, 1);
    const minPrice = Math.max(0, rawMin - spread * 0.15);
    const maxPrice = rawMax + spread * 0.15;
    const plotWidth = WIDTH - PADDING.left - PADDING.right;
    const salesBaseline = HEIGHT - PADDING.bottom;
    const salesTop = salesBaseline - SALES_SECTION_HEIGHT;
    const plotHeight = salesTop - SALES_SECTION_GAP - PADDING.top;

    const normalized = points.map<ChartPoint>((point, index) => {
      const x =
        PADDING.left +
        (points.length <= 1 ? 0 : (index / (points.length - 1)) * plotWidth);
      const normalizedPrice =
        point.avg_price > 0
          ? (point.avg_price - minPrice) / (maxPrice - minPrice)
          : 0;

      return {
        ...point,
        x,
        y:
          point.avg_price > 0
            ? PADDING.top + plotHeight * (1 - normalizedPrice)
            : PADDING.top + plotHeight,
      };
    });
    const linePoints = normalized.filter((point) => point.avg_price > 0);
    const pathPoints = resamplePathPoints(linePoints);
    const linePath = createSmoothLinePath(pathPoints);
    const baseline = PADDING.top + plotHeight;
    const areaPath =
      pathPoints.length > 0
        ? `${linePath} L ${pathPoints.at(-1)?.x ?? 0} ${baseline} L ${
            pathPoints[0].x
          } ${baseline} Z`
        : "";

    const maxSales = Math.max(
      ...points.map((point) => Math.max(0, point.sales_count)),
      1,
    );
    const pointStep = plotWidth / Math.max(points.length - 1, 1);
    const barWidth = Math.min(22, Math.max(3, pointStep * 0.56));
    const salesBars = normalized.map<SalesBar>((point) => {
      const height =
        (Math.max(0, point.sales_count) / maxSales) * SALES_SECTION_HEIGHT;
      return {
        x: point.x - barWidth / 2,
        y: salesBaseline - height,
        width: barWidth,
        height,
      };
    });

    return {
      points: normalized,
      linePath,
      areaPath,
      minPrice,
      maxPrice,
      baseline,
      salesTop,
      salesBaseline,
      salesBars,
      plotWidth,
      plotHeight,
      interactiveHeight: salesBaseline - PADDING.top,
    };
  }, [points]);

  if (chart === null) {
    return (
      <div className="chart-empty">
        История за выбранный период пока отсутствует
      </div>
    );
  }

  const hovered =
    hoveredIndex === null ? null : chart.points[hoveredIndex] ?? null;
  const activeRange =
    dragStartIndex !== null && dragEndIndex !== null
      ? normalizeRange(dragStartIndex, dragEndIndex)
      : selectedRange;
  const selectionRect = activeRange
    ? (() => {
        const start = chart.points[activeRange.startIndex];
        const end = chart.points[activeRange.endIndex];
        if (!start || !end) {
          return null;
        }

        return {
          x: start.x,
          width: Math.max(0, end.x - start.x),
        };
      })()
    : null;
  const xLabels = chart.points.filter((_, index) => {
    if (chart.points.length <= 5) {
      return true;
    }
    const step = Math.max(1, Math.ceil((chart.points.length - 1) / 7));
    return index % step === 0 || index === chart.points.length - 1;
  });
  const gradientId = `history-area-${id.replace(/[^a-zA-Z0-9_-]/g, "")}`;
  const salesGradientId = `history-sales-${id.replace(/[^a-zA-Z0-9_-]/g, "")}`;
  const clipId = `history-clip-${id.replace(/[^a-zA-Z0-9_-]/g, "")}`;
  const pointIndexFromPointer = (event: ReactPointerEvent<SVGSVGElement>) => {
    const bounds = event.currentTarget.getBoundingClientRect();
    const pointerX = ((event.clientX - bounds.left) / bounds.width) * WIDTH;
    const ratio = Math.min(
      1,
      Math.max(0, (pointerX - PADDING.left) / Math.max(chart.plotWidth, 1)),
    );
    return Math.round(ratio * Math.max(chart.points.length - 1, 0));
  };
  const finishSelection = (event: ReactPointerEvent<SVGSVGElement>) => {
    if (dragStartIndex === null) {
      return;
    }

    const endIndex = pointIndexFromPointer(event);
    if (endIndex !== dragStartIndex) {
      onRangeChange(normalizeRange(dragStartIndex, endIndex));
    }
    setDragStartIndex(null);
    setDragEndIndex(null);
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
  };

  return (
    <div className="history-chart-wrap">
      <svg
        className="history-chart"
        viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
        preserveAspectRatio="none"
        role="img"
        aria-label="График средней цены и количества продаж"
        onPointerLeave={() => {
          if (dragStartIndex === null) {
            setHoveredIndex(null);
          }
        }}
        onPointerMove={(event) => {
          const pointIndex = pointIndexFromPointer(event);
          setHoveredIndex(pointIndex);
          if (dragStartIndex !== null) {
            setDragEndIndex(pointIndex);
          }
        }}
        onPointerDown={(event) => {
          if (event.button !== 0 || chart.points.length < 2) {
            return;
          }

          const pointIndex = pointIndexFromPointer(event);
          event.preventDefault();
          event.currentTarget.setPointerCapture(event.pointerId);
          setHoveredIndex(pointIndex);
          setDragStartIndex(pointIndex);
          setDragEndIndex(pointIndex);
        }}
        onPointerUp={finishSelection}
        onPointerCancel={() => {
          setDragStartIndex(null);
          setDragEndIndex(null);
        }}
      >
        <defs>
          <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#f3f5f8" stopOpacity="0.3" />
            <stop offset="72%" stopColor="#f3f5f8" stopOpacity="0.07" />
            <stop offset="100%" stopColor="#f3f5f8" stopOpacity="0.01" />
          </linearGradient>
          <linearGradient id={salesGradientId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#f1f3f6" stopOpacity="0.58" />
            <stop offset="100%" stopColor="#f1f3f6" stopOpacity="0.12" />
          </linearGradient>
          <clipPath id={clipId}>
            <rect
              x={PADDING.left}
              y={PADDING.top}
              width={chart.plotWidth}
              height={chart.interactiveHeight}
              rx="9"
            />
          </clipPath>
        </defs>

        {[0, 1 / 6, 2 / 6, 3 / 6, 4 / 6, 5 / 6, 1].map((ratio) => {
          const y = PADDING.top + chart.plotHeight * ratio;
          const value =
            chart.maxPrice - (chart.maxPrice - chart.minPrice) * ratio;
          return (
            <g key={ratio}>
              <line
                className="chart-grid-line"
                x1={PADDING.left}
                x2={WIDTH - PADDING.right}
                y1={y}
                y2={y}
              />
              <text className="chart-axis-label" x={4} y={y + 4}>
                {formatPrice(value, { maximumFractionDigits: 0 })}
              </text>
            </g>
          );
        })}

        {xLabels.map((point) => (
          <line
            className="chart-grid-line chart-grid-line-vertical"
            key={`grid-${point.date}`}
            x1={point.x}
            x2={point.x}
            y1={PADDING.top}
            y2={chart.salesBaseline}
          />
        ))}

        <line
          className="chart-sales-grid-line"
          x1={PADDING.left}
          x2={WIDTH - PADDING.right}
          y1={chart.salesTop}
          y2={chart.salesTop}
        />
        <line
          className="chart-sales-grid-line"
          x1={PADDING.left}
          x2={WIDTH - PADDING.right}
          y1={chart.salesBaseline}
          y2={chart.salesBaseline}
        />
        <g clipPath={`url(#${clipId})`}>
          {selectionRect && (
            <>
              <rect
                className="chart-range-selection"
                x={selectionRect.x}
                y={PADDING.top}
                width={selectionRect.width}
                height={chart.interactiveHeight}
              />
              <line
                className="chart-range-boundary"
                x1={selectionRect.x}
                x2={selectionRect.x}
                y1={PADDING.top}
                y2={chart.salesBaseline}
              />
              <line
                className="chart-range-boundary"
                x1={selectionRect.x + selectionRect.width}
                x2={selectionRect.x + selectionRect.width}
                y1={PADDING.top}
                y2={chart.salesBaseline}
              />
            </>
          )}
          <motion.path
            className="history-area"
            fill={`url(#${gradientId})`}
            initial={false}
            animate={{ d: chart.areaPath }}
            transition={{ duration: 0.58, ease: [0.22, 1, 0.36, 1] }}
          />
          <motion.path
            className="history-line"
            initial={false}
            animate={{ d: chart.linePath }}
            transition={{ duration: 0.58, ease: [0.22, 1, 0.36, 1] }}
          />
          <g className="chart-sales-bars">
            {chart.salesBars.map((bar, index) => (
              <rect
                className="chart-sales-bar"
                fill={`url(#${salesGradientId})`}
                height={bar.height}
                key={`${chart.points[index]?.date ?? index}-sales`}
                rx={Math.min(bar.width / 2, 3)}
                width={bar.width}
                x={bar.x}
                y={bar.y}
              />
            ))}
          </g>
        </g>

        {xLabels.map((point) => (
          <text
            className="chart-axis-label chart-axis-label-x"
            key={point.date}
            x={point.x}
            y={HEIGHT - 6}
            textAnchor="middle"
          >
            {point.label}
          </text>
        ))}

        {hovered && (
          <g className="chart-tooltip" pointerEvents="none">
            <line
              x1={hovered.x}
              x2={hovered.x}
              y1={PADDING.top}
              y2={chart.salesBaseline}
            />
            <circle cx={hovered.x} cy={hovered.y} r="4" />
            <g
              transform={`translate(${Math.min(
                WIDTH - 132,
                Math.max(PADDING.left, hovered.x - 56),
              )}, ${PADDING.top + 10})`}
            >
              <rect width="122" height="58" rx="6" />
              <text x="10" y="17">
                {hovered.label}
              </text>
              <text className="chart-tooltip-value" x="10" y="35">
                {formatPrice(hovered.avg_price)}
              </text>
              <text x="10" y="50">
                {hovered.sales_count} продаж
              </text>
            </g>
          </g>
        )}
      </svg>
    </div>
  );
}
